import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ExecutionLogEntry } from "@/types/workflow";
import { Json } from "@/integrations/supabase/types";

interface StartWorkflowParams {
  workflowId: string;
  triggerData?: Record<string, unknown>;
}

// Convert execution log to Json type
const toJson = (log: ExecutionLogEntry[]): Json => {
  return log as unknown as Json;
};

export const useStartWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workflowId, triggerData }: StartWorkflowParams) => {
      console.log("[useStartWorkflow] Starting workflow:", workflowId);

      // Create initial execution log
      const initialLog: ExecutionLogEntry[] = [
        {
          timestamp: new Date().toISOString(),
          level: "info",
          message: "Workflow gestart",
          node: "trigger",
        },
      ];

      const { data, error } = await supabase
        .from("workflow_executions")
        .insert({
          workflow_id: workflowId,
          status: "running",
          trigger_data: triggerData as Json || null,
          execution_log: toJson(initialLog),
        })
        .select()
        .single();

      if (error) {
        console.error("[useStartWorkflow] Error:", error);
        throw error;
      }

      console.log("[useStartWorkflow] Execution created:", data.id);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow-executions"] });
      queryClient.invalidateQueries({ queryKey: ["active-executions"] });
      queryClient.invalidateQueries({ queryKey: ["execution-metrics"] });
    },
  });
};

interface UpdateExecutionParams {
  executionId: string;
  status: "running" | "success" | "failed" | "cancelled" | "waiting";
  errorMessage?: string;
  logEntry?: ExecutionLogEntry;
}

export const useUpdateExecution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ executionId, status, errorMessage, logEntry }: UpdateExecutionParams) => {
      console.log("[useUpdateExecution] Updating execution:", executionId, status);

      // First get current execution to append to log
      const { data: current, error: fetchError } = await supabase
        .from("workflow_executions")
        .select("execution_log")
        .eq("id", executionId)
        .single();

      if (fetchError) throw fetchError;

      // Build updated log
      const currentLog = (current.execution_log as unknown as ExecutionLogEntry[]) || [];
      const updatedLog = logEntry ? [...currentLog, logEntry] : currentLog;

      // Build update payload
      const updatePayload: Record<string, unknown> = {
        status,
        execution_log: toJson(updatedLog),
      };

      if (status === "success" || status === "failed" || status === "cancelled") {
        updatePayload.completed_at = new Date().toISOString();
      }

      if (errorMessage) {
        updatePayload.error_message = errorMessage;
      }

      const { error } = await supabase
        .from("workflow_executions")
        .update(updatePayload)
        .eq("id", executionId);

      if (error) {
        console.error("[useUpdateExecution] Error:", error);
        throw error;
      }

      console.log("[useUpdateExecution] Updated execution:", executionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow-executions"] });
      queryClient.invalidateQueries({ queryKey: ["active-executions"] });
      queryClient.invalidateQueries({ queryKey: ["execution-metrics"] });
    },
  });
};

// Simulate a full workflow execution with realistic timing
export const useSimulateWorkflow = () => {
  const startWorkflow = useStartWorkflow();
  const updateExecution = useUpdateExecution();

  return useMutation({
    mutationFn: async (workflowId: string) => {
      console.log("[useSimulateWorkflow] Simulating workflow:", workflowId);

      // Start the workflow
      const execution = await startWorkflow.mutateAsync({
        workflowId,
        triggerData: {
          type: "manual",
          source: "ui",
          triggered_by: "user",
          timestamp: new Date().toISOString(),
        },
      });

      // Simulate processing steps with delays
      const steps = [
        { delay: 1000, message: "Trigger ontvangen", node: "trigger" },
        { delay: 1500, message: "Data ophalen...", node: "fetch_data" },
        { delay: 2000, message: "Data verwerken...", node: "process" },
        { delay: 1000, message: "Resultaten valideren", node: "validate" },
      ];

      for (const step of steps) {
        await new Promise(resolve => setTimeout(resolve, step.delay));
        
        await updateExecution.mutateAsync({
          executionId: execution.id,
          status: "running",
          logEntry: {
            timestamp: new Date().toISOString(),
            level: "info",
            message: step.message,
            node: step.node,
            duration_ms: step.delay,
          },
        });
      }

      // Random success/failure (90% success rate)
      const isSuccess = Math.random() > 0.1;

      await new Promise(resolve => setTimeout(resolve, 500));

      if (isSuccess) {
        await updateExecution.mutateAsync({
          executionId: execution.id,
          status: "success",
          logEntry: {
            timestamp: new Date().toISOString(),
            level: "info",
            message: "Workflow succesvol afgerond",
            node: "end",
          },
        });
      } else {
        await updateExecution.mutateAsync({
          executionId: execution.id,
          status: "failed",
          errorMessage: "Simulatiefout: Random failure voor demo doeleinden",
          logEntry: {
            timestamp: new Date().toISOString(),
            level: "error",
            message: "Workflow gefaald",
            node: "error_handler",
          },
        });
      }

      return execution.id;
    },
  });
};
